import { Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Chip,
  DetailHeader,
  Panel,
  toneColor,
  type Tone,
} from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useRealtimeEvent } from "@/realtime/useRealtime";

const statusTone: Record<string, Tone> = {
  ACTIVE: "green",
  COMPLETED: "green",
  PAUSED: "amber",
  FAILED: "red",
  INTERRUPTED: "red",
};

const logTone: Record<string, Tone> = {
  START: "cyan",
  PLAN: "cyan",
  CHECKPOINT: "green",
  ARTIFACT: "cyan",
  DISCOVERY: "amber",
  BLOCKER: "red",
  NOTE: "dim",
  FINISH: "green",
};

function formatDuration(startedAt: string, endedAt: string | null): string {
  const ms =
    (endedAt ? new Date(endedAt).getTime() : Date.now()) -
    new Date(startedAt).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Session Detail (spec §46, /Design SESSION DETAIL). */
export function SessionDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["session", id],
    queryFn: () => api.session(id),
  });
  useRealtimeEvent((m) => {
    if (m.type === "project.section.changed" && m.section === "sessions") {
      void queryClient.invalidateQueries({ queryKey: ["session", id] });
    }
  });

  const s = query.data?.session;
  if (!s) return null;
  const tone = statusTone[s.status] ?? "dim";
  const discoveries = s.logs.filter((l) => l.kind === "DISCOVERY");
  const blockers = s.logs.filter((l) => l.kind === "BLOCKER");

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={
          <Link to="/workers" style={{ color: "inherit" }}>
            {cs.sessionDetail.back}
          </Link>
        }
        title={cs.sessionDetail.title(`#${s.id.slice(-5)}`)}
        meta={
          <span data-testid="session-status">
            <Chip tone={tone}>
              {s.status === "ACTIVE" ? "● PRACUJE" : s.status}
            </Chip>
          </span>
        }
      />

      {/* meta strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <MetaTile label={cs.sessionDetail.workerCol} value={s.workerName} />
        <Link
          to="/projects/$slug/tasks/$number"
          params={{ slug: s.projectSlug, number: String(s.taskNumber) }}
        >
          <MetaTile
            label={cs.sessionDetail.task}
            value={`#${s.taskNumber} ${s.taskTitle} →`}
            cyan
          />
        </Link>
        <MetaTile
          label={cs.sessionDetail.model}
          value={s.models.join(", ") || "—"}
        />
        <MetaTile
          label={
            s.weekPctSpent != null
              ? cs.sessionDetail.weekCost
              : cs.sessionDetail.tokens
          }
          value={
            s.weekPctSpent != null
              ? `${s.weekPctSpent.toFixed(1).replace(".", ",")} %`
              : s.tokensTotal != null
                ? `${Math.round(s.tokensTotal / 1000)}k`
                : "—"
          }
          sub={
            s.weekPctSpent != null && s.tokensTotal != null
              ? `${Math.round(s.tokensTotal / 1000)}k tokens ${cs.sessionDetail.tokensNote}`
              : cs.sessionDetail.tokensNote
          }
        />
        {/* Kroky jsou to, co se platí: v každém se přečte celý kontext znovu.
            Baseline pro srovnání drží podtitulek — bez něj je číslo bez měřítka. */}
        <MetaTile
          label={cs.sessionDetail.steps}
          value={s.turns != null ? String(s.turns) : "—"}
          sub={
            s.turns != null && s.avgContextTokens != null
              ? cs.sessionDetail.stepsNote(
                  Math.round(s.avgContextTokens / 1000),
                )
              : cs.sessionDetail.stepsUnknown
          }
        />
        <MetaTile
          label={cs.sessionDetail.duration}
          value={formatDuration(s.startedAt, s.endedAt)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_1fr]">
        <div className="flex flex-col gap-4">
          <Panel className="p-4">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.sessionDetail.log}
            </div>
            {s.logs.map((l) => (
              <div
                key={l.id}
                className="flex gap-3 py-1.5 text-[12px]"
                style={{
                  borderTop:
                    "1px solid color-mix(in srgb, var(--accent) 5%, transparent)",
                }}
              >
                <span
                  className="w-14 flex-none"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {relativeTime(l.createdAt)}
                </span>
                <span
                  className="min-w-0 break-words"
                  style={{ color: toneColor[logTone[l.kind] ?? "dim"] }}
                >
                  {l.text}
                </span>
              </div>
            ))}
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.sessionDetail.checkpoints}
            </div>
            {s.plan.length === 0 ? (
              <div
                className="text-[12px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {cs.task.planEmpty}
              </div>
            ) : (
              s.plan.map((p, i) => (
                <div
                  key={i}
                  className="mb-1.5 flex gap-2.5 font-mono text-[12px]"
                >
                  <span
                    style={{
                      color:
                        p.state === "DONE"
                          ? "var(--positive)"
                          : p.state === "DOING"
                            ? "var(--accent)"
                            : "var(--text-tertiary)",
                    }}
                  >
                    {p.state === "DONE"
                      ? "[x]"
                      : p.state === "DOING"
                        ? "[>]"
                        : "[ ]"}
                  </span>
                  <span
                    style={{
                      color:
                        p.state === "DONE"
                          ? "var(--text-secondary)"
                          : "var(--text-secondary)",
                    }}
                  >
                    {p.text}
                  </span>
                </div>
              ))
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-4">
          <Panel className="p-4">
            <div className="af-label" style={{ color: "var(--accent)" }}>
              {cs.sessionDetail.chain}
            </div>
            <div
              className="mb-3 mt-0.5 text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {cs.sessionDetail.chainNote}
            </div>
            {s.chain.map((c) => (
              <div key={c.id} className="flex gap-3 pb-2.5">
                <div className="flex w-2.5 flex-none flex-col items-center">
                  <div
                    className="mt-1 h-2 w-2 rounded-full"
                    style={{
                      background: toneColor[statusTone[c.status] ?? "dim"],
                    }}
                  />
                  <div
                    className="mt-1 w-px flex-1"
                    style={{
                      background:
                        "color-mix(in srgb, var(--accent) 12%, transparent)",
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <div
                    className="break-words text-[12px] font-bold"
                    style={{
                      color:
                        c.id === s.id ? "var(--accent)" : "var(--text-primary)",
                    }}
                  >
                    {c.workerName} {c.id === s.id ? "· tato session" : ""}
                  </div>
                  <div
                    className="mt-0.5 text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {c.status} · {relativeTime(c.startedAt)}
                    {c.endedAt ? ` → ${relativeTime(c.endedAt)}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </Panel>

          {s.handoff ? (
            <Panel tone="green" className="p-4" data-testid="session-handoff">
              <div
                className="af-label mb-2"
                style={{ color: "var(--positive)" }}
              >
                {cs.sessionDetail.handoff}
              </div>
              <div
                className="break-words text-[12px] leading-[1.7]"
                style={{ color: "var(--text-primary)" }}
              >
                {s.handoff.state ? <p>{s.handoff.state}</p> : null}
                {s.handoff.completed.length ? (
                  <p>
                    ✓ {cs.sessionDetail.completed}:{" "}
                    {s.handoff.completed.join(" · ")}
                  </p>
                ) : null}
                {s.handoff.remaining.length ? (
                  <p>
                    □ {cs.sessionDetail.remaining}:{" "}
                    {s.handoff.remaining.join(" · ")}
                  </p>
                ) : null}
                {s.handoff.branch ? <p>branch: {s.handoff.branch}</p> : null}
                {s.handoff.testsRun.length ? (
                  <p>testy: {s.handoff.testsRun.join(" · ")}</p>
                ) : null}
                {s.handoff.nextAction ? (
                  <p style={{ color: "var(--positive)" }}>
                    → {cs.sessionDetail.nextAction}: {s.handoff.nextAction}
                  </p>
                ) : null}
              </div>
            </Panel>
          ) : null}

          {blockers.length ? (
            <Panel tone="red" className="p-4" data-testid="session-blockers">
              <div
                className="af-label mb-2"
                style={{ color: "var(--critical)" }}
              >
                {cs.sessionDetail.blockers}
              </div>
              {blockers.map((b) => (
                <div
                  key={b.id}
                  className="mb-1 text-[12px] leading-relaxed"
                  style={{ color: "var(--text-primary)" }}
                >
                  {b.resolvedAt ? "✓" : "⚠"} {b.text}
                  {b.resolvedAt ? (
                    <span style={{ color: "var(--positive)" }}>
                      {" "}
                      — {cs.sessionDetail.blockerResolved(b.resolvedBy ?? "")}
                      {b.resolutionNote ? `: ${b.resolutionNote}` : ""}
                    </span>
                  ) : null}
                </div>
              ))}
            </Panel>
          ) : null}

          {discoveries.length ? (
            <Panel tone="amber" className="p-4">
              <div
                className="af-label mb-2"
                style={{ color: "var(--warning)" }}
              >
                {cs.sessionDetail.discoveries}
              </div>
              {discoveries.map((d) => (
                <div
                  key={d.id}
                  className="mb-1 text-[12px] leading-relaxed"
                  style={{ color: "var(--text-primary)" }}
                >
                  {d.text}
                </div>
              ))}
            </Panel>
          ) : null}

          <Panel className="p-4">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.sessionDetail.artifacts}
            </div>
            {s.artifacts.length === 0 ? (
              <div
                className="text-[12px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                —
              </div>
            ) : (
              s.artifacts.map((a) => (
                <div
                  key={a.id}
                  className="mb-1.5 flex justify-between gap-3 text-[11px]"
                >
                  <span
                    className="flex-none"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {a.kind}
                  </span>
                  <span
                    className="min-w-0 break-words text-right"
                    style={{ color: "var(--accent)" }}
                  >
                    {a.ref ?? a.label}
                  </span>
                </div>
              ))
            )}
          </Panel>

          <div
            className="text-[11px] leading-[1.7]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.sessionDetail.footer}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaTile({
  label,
  value,
  sub,
  cyan,
}: {
  label: string;
  value: string;
  sub?: string;
  cyan?: boolean;
}) {
  return (
    <Panel className="px-3.5 py-2.5">
      <div
        className="text-[11px] tracking-[0.16em]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {label}
      </div>
      <div
        className="mt-0.5 truncate text-[12px]"
        style={{ color: cyan ? "var(--accent)" : "var(--text-primary)" }}
      >
        {value}
        {sub ? (
          <span
            className="ml-1 text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {sub}
          </span>
        ) : null}
      </div>
    </Panel>
  );
}
